import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-[#194d32] hover:text-[hsl(var(--accent))] transition-colors mb-8 font-medium"
      >
        <ArrowLeft size={18} />
        Back to Home
      </Link>

      <h1 className="headline text-4xl sm:text-5xl md:text-6xl text-[hsl(var(--navy))] mb-4">
        Privacy Policy
      </h1>
      <p className="text-[hsl(var(--charcoal))]/60 text-sm mb-10">
        Last Updated: September 2026
      </p>

      <div className="space-y-8 text-[hsl(var(--charcoal))]/80 leading-relaxed">
        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            Information We Collect
          </h2>
          <p>We collect information that you voluntarily provide to us, including:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong>Personal Information:</strong> Name, phone number, email address, physical address</li>
            <li><strong>Project Information:</strong> Type of project, project description, property details</li>
            <li><strong>Communication Data:</strong> Records of your communications with us</li>
          </ul>
        </section>

        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            How We Use Your Information
          </h2>
          <p>We use your information for the following purposes:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>To provide and deliver our concrete construction services</li>
            <li>To process quotes, estimates, and contracts</li>
            <li>To communicate with you about your project</li>
            <li>To respond to your inquiries and customer service requests</li>
            <li>To improve our website and services</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            Information Sharing
          </h2>
          <p>
            We do <strong>not</strong> sell, trade, or rent your personal
            information to third parties. We may share your information only in
            the following circumstances:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong>Service Providers:</strong> With vendors who assist us in providing our services (e.g., payment processors)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          </ul>
        </section>

        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            Data Security
          </h2>
          <p>
            We implement appropriate technical and organizational measures to
            protect your personal information against unauthorized access,
            alteration, disclosure, or destruction. However, no method of
            transmission over the internet is 100% secure, and we cannot
            guarantee absolute security.
          </p>
        </section>

        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            Your Rights
          </h2>
          <p>Under applicable law, you may have the following rights:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request that we correct inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request that we delete your personal information, subject to legal limitations</li>
            <li><strong>Opt-Out:</strong> Opt out of receiving promotional communications from us</li>
          </ul>
          <p className="mt-2">
            To exercise these rights, please contact us at{" "}
            <a href="mailto:naturalconcrete.contruction@gmail.com" className="text-[#194d32] hover:text-[hsl(var(--accent))] transition-colors">
              naturalconcrete.contruction@gmail.com
            </a>
          </p>
        </section>

        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            Cookies
          </h2>
          <p>
            Our website may use cookies to enhance your browsing experience.
            Cookies are small data files stored on your device. You can choose
            to disable cookies in your browser settings, but this may affect the
            functionality of our website.
          </p>
          <p className="mt-2">We use cookies for:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Analyzing website traffic and user behavior</li>
            <li>Remembering your preferences</li>
            <li>Improving website performance</li>
          </ul>
        </section>

        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            Children&apos;s Privacy
          </h2>
          <p>
            Our services are not directed to children under the age of 13. We do
            not knowingly collect personal information from children. If we
            become aware that we have collected personal information from a
            child under 13, we will take steps to delete that information.
          </p>
        </section>

        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify
            you of any changes by posting the new policy on this page. We
            encourage you to review this policy periodically for any changes.
          </p>
        </section>

        <section>
          <h2 className="headline text-2xl text-[hsl(var(--navy))] mb-3">
            Contact Us
          </h2>
          <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
          <div className="mt-3 space-y-2">
            <p><strong>Naturals Concrete Construction</strong></p>
            <p>📧 <a href="mailto:naturalconcrete.contruction@gmail.com" className="text-[#194d32] hover:text-[hsl(var(--accent))] transition-colors">
              naturalconcrete.contruction@gmail.com
            </a></p>
            <p>📞 <a href="tel:+14794227180" className="text-[#194d32] hover:text-[hsl(var(--accent))] transition-colors">
              479-422-7180
            </a></p>
          </div>
        </section>
      </div>

      <div className="mt-12 pt-8 border-t border-[hsl(var(--concrete))] text-center text-[hsl(var(--charcoal))]/50 text-sm">
        &copy; {new Date().getFullYear()} Naturals Concrete Construction. All rights reserved.
      </div>
    </div>
  );
}